'use client';

import { useQuery } from '@tanstack/react-query';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import { getSalesAgentCalendar } from 'services/user';
import { FALLBACK_CALENDAR_URL, OLD_GENERIC_ROUND_ROBIN } from 'utils/calendarConstants';

interface RestrictedOfferingDialogProps {
  open: boolean;
  onClose: () => void;
}

interface SalesAgentCalendar {
  calendarUrl?: string | null;
  salesAgentFirstName?: string | null;
  salesAgentLastName?: string | null;
}

const APPROVED_CALENDAR_ORIGINS = new Set([
  'https://calendly.com',
  'https://hubspot.com',
  'https://meetings.hubspot.com',
  'https://meetings-na2.hubspot.com',
]);

const getAdvisorName = (calendarData?: SalesAgentCalendar) => {
  const fullName = [calendarData?.salesAgentFirstName, calendarData?.salesAgentLastName]
    .map((namePart) => namePart?.trim())
    .filter(Boolean)
    .join(' ');

  return fullName || 'Private Equity Associate';
};

const getContactUrl = (calendarUrl?: string | null) => {
  if (!calendarUrl || calendarUrl === OLD_GENERIC_ROUND_ROBIN) return FALLBACK_CALENDAR_URL;

  try {
    const url = new URL(calendarUrl);
    return APPROVED_CALENDAR_ORIGINS.has(url.origin) ? url.toString() : FALLBACK_CALENDAR_URL;
  } catch {
    return FALLBACK_CALENDAR_URL;
  }
};

const StyledDialog = styled(Dialog)({
  '& .MuiBackdrop-root': {
    background: 'rgba(0, 14, 11, 0.64)',
    backdropFilter: 'blur(7px)',
  },
  '& .MuiDialog-paper': {
    position: 'relative',
    width: 'min(520px, calc(100% - 32px))',
    margin: '16px',
    padding: '48px 44px 40px',
    overflow: 'hidden',
    border: `1px solid ${COLORS.edge}`,
    borderTop: `3px solid ${COLORS.gold}`,
    borderRadius: '24px',
    background: COLORS.cream,
    boxShadow: '0 28px 80px rgba(0, 14, 11, 0.32)',
    textAlign: 'center',
    '@media (max-width: 640px)': {
      padding: '42px 24px 28px',
      borderRadius: '20px',
    },
  },
});

const CloseButton = styled('button')({
  position: 'absolute',
  top: '16px',
  right: '16px',
  width: '34px',
  height: '34px',
  border: `1px solid ${COLORS.edge}`,
  borderRadius: '50%',
  background: 'rgba(255, 252, 246, 0.72)',
  color: COLORS.ink,
  fontFamily: FONTS.sans,
  fontSize: '20px',
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s',
  '&:hover': {
    background: '#FFFFFF',
    borderColor: COLORS.gold,
  },
});

const LockMark = styled('div')({
  width: '58px',
  height: '58px',
  margin: '0 auto 20px',
  display: 'grid',
  placeItems: 'center',
  border: '1px solid rgba(181, 138, 75, 0.5)',
  borderRadius: '18px',
  background: 'rgba(181, 138, 75, 0.09)',
  color: COLORS.tealInk,
});

const Eyebrow = styled('div')({
  marginBottom: '10px',
  color: COLORS.gold,
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
});

const Title = styled('h2')({
  margin: '0 0 14px',
  color: COLORS.ink,
  fontFamily: FONTS.serif,
  fontSize: '36px',
  fontWeight: 400,
  lineHeight: 1.05,
  letterSpacing: '-0.02em',
  '@media (max-width: 640px)': { fontSize: '31px' },
});

const Copy = styled('p')({
  maxWidth: '390px',
  margin: '0 auto 24px',
  color: COLORS.ink2,
  fontFamily: FONTS.sans,
  fontSize: '16px',
  lineHeight: 1.65,
});

const AdvisorCard = styled('div')({
  marginBottom: '20px',
  padding: '20px',
  border: `1px solid ${COLORS.edge}`,
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.48)',
});

const AdvisorLabel = styled('div')({
  marginBottom: '6px',
  color: COLORS.gold,
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
});

const AdvisorName = styled('div')({
  color: COLORS.ink,
  fontFamily: FONTS.serif,
  fontSize: '24px',
  lineHeight: 1.2,
});

const ContactAction = styled('a')({
  width: '100%',
  minHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  borderRadius: '12px',
  background: COLORS.tealInk,
  color: '#FFFFFF',
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textDecoration: 'none',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'background 0.15s, transform 0.15s',
  '&:hover': { background: COLORS.night, transform: 'translateY(-1px)' },
});

const DismissAction = styled('button')({
  marginTop: '10px',
  padding: '10px 16px',
  border: 0,
  background: 'transparent',
  color: COLORS.ink2,
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  '&:hover': { color: COLORS.ink },
});

const LockIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10M6 10h12v10H6V10Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 14v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

export default function RestrictedOfferingDialog({ open, onClose }: RestrictedOfferingDialogProps) {
  const { data: calendarData } = useQuery<SalesAgentCalendar>({
    queryKey: ['salesAgentCalendar'],
    queryFn: getSalesAgentCalendar,
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  const advisorName = getAdvisorName(calendarData);
  const advisorFirstName = calendarData?.salesAgentFirstName?.trim();
  const contactUrl = getContactUrl(calendarData?.calendarUrl);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="restricted-offering-dialog-title"
      data-cy="restricted-offering-dialog"
    >
      <CloseButton type="button" aria-label="Close restricted offering message" onClick={onClose}>
        ×
      </CloseButton>
      <LockMark>
        <LockIcon />
      </LockMark>
      <Eyebrow>Limited access</Eyebrow>
      <Title id="restricted-offering-dialog-title">A private opportunity</Title>
      <Copy>
        Access is limited to approved investors. Your associate can help confirm eligibility and
        next steps.
      </Copy>
      <AdvisorCard data-cy="restricted-offering-advisor">
        <AdvisorLabel>Your Private Equity Associate</AdvisorLabel>
        <AdvisorName>{advisorName}</AdvisorName>
      </AdvisorCard>
      <ContactAction
        href={contactUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-cy="restricted-offering-contact-advisor"
      >
        Book with {advisorFirstName || 'your associate'}
      </ContactAction>
      <DismissAction type="button" onClick={onClose}>
        Got it
      </DismissAction>
    </StyledDialog>
  );
}
